import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Users, TrendingUp, Trophy, XCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { cn } from '../lib/cn';
import { useStore } from '../store/useStore';
import { leadService } from '../lib/leadService';
import {
    Lead, LEAD_STAGES, LEAD_STAGE_LABELS, LEAD_SOURCE_LABELS, LEAD_SOURCE_KEYS,
    LEAD_LOST_REASON_LABELS, leadStaleness,
} from '../types/leads.types';

const DAY = 86_400_000;

function KpiCard({ icon: Icon, label, value, sub, color }: {
    icon: React.ElementType; label: string; value: string | number; sub?: string; color: string;
}) {
    return (
        <Card className="p-3 md:p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs">
                <Icon className={cn('w-4 h-4', color)} /> {label}
            </div>
            <div className="text-xl md:text-2xl font-bold mt-1">{value}</div>
            {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
        </Card>
    );
}

// barra orizzontale semplice (no libreria grafici)
function BarRow({ label, count, total, colorClass }: { label: string; count: number; total: number; colorClass: string }) {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <div className="flex items-center gap-2 text-sm">
            <div className="w-24 sm:w-40 truncate text-gray-600">{label}</div>
            <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                <div className={cn('h-full rounded-full', colorClass)} style={{ width: `${Math.max(pct, count > 0 ? 3 : 0)}%` }} />
            </div>
            <div className="w-16 flex-shrink-0 text-right text-gray-500 tabular-nums">{count} · {pct}%</div>
        </div>
    );
}

export default function LeadsDashboardPage() {
    const navigate = useNavigate();
    const { addNotification } = useStore();
    const [leads, setLeads] = useState<Lead[]>([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            setLeads(await leadService.getLeads());
        } catch (error) {
            console.error(error);
            addNotification({ type: 'error', title: 'Errore', message: 'Impossibile caricare i dati' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const s = useMemo(() => {
        const total = leads.length;
        const byStage: Record<string, number> = {};
        LEAD_STAGES.forEach((st) => { byStage[st.key] = 0; });
        leads.forEach((l) => { byStage[l.stage] = (byStage[l.stage] || 0) + 1; });

        const won = byStage['vinto'] || 0;
        const lost = byStage['perso'] || 0;
        const active = total - won - lost;
        const conversion = won + lost > 0 ? Math.round((won / (won + lost)) * 100) : 0;
        const recontact = leads.filter((l) => leadStaleness(l).level !== 'fresh').length;

        // fonte
        const bySource = LEAD_SOURCE_KEYS.map((src) => ({
            key: src, label: LEAD_SOURCE_LABELS[src], count: leads.filter((l) => l.source === src).length,
        })).filter((r) => r.count > 0).sort((a, b) => b.count - a.count);

        // persi per motivo
        const lostMap: Record<string, number> = {};
        leads.filter((l) => l.stage === 'perso' && l.lost_reason).forEach((l) => {
            const key = (l.lost_reason as string).split(':')[0];
            lostMap[key] = (lostMap[key] || 0) + 1;
        });
        const lostByReason = Object.entries(lostMap)
            .map(([key, count]) => ({ key, label: LEAD_LOST_REASON_LABELS[key] || key, count }))
            .sort((a, b) => b.count - a.count);

        // tempo medio chiusura (giorni) per lead vinti/persi
        const closed = leads.filter((l) => (l.stage === 'vinto' || l.stage === 'perso') && l.stage_changed_at);
        const avgClose = closed.length
            ? Math.round(closed.reduce((acc, l) => acc + (new Date(l.stage_changed_at as string).getTime() - new Date(l.created_at).getTime()) / DAY, 0) / closed.length)
            : null;

        // questo mese
        const now = new Date();
        const mStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
        const newThisMonth = leads.filter((l) => new Date(l.created_at).getTime() >= mStart).length;
        const wonThisMonth = leads.filter((l) => l.stage === 'vinto' && l.stage_changed_at && new Date(l.stage_changed_at).getTime() >= mStart).length;

        return { total, byStage, won, lost, active, conversion, recontact, bySource, lostByReason, avgClose, newThisMonth, wonThisMonth };
    }, [leads]);

    return (
        <div className="mx-auto w-full max-w-6xl py-2 md:py-6 space-y-4 md:space-y-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <Button variant="ghost" className="mb-2 pl-0" onClick={() => navigate('/leads')}>
                        <ArrowLeft className="w-4 h-4 mr-2" /> Torna ai lead
                    </Button>
                    <h1 className="text-2xl md:text-3xl font-bold">Statistiche Lead</h1>
                </div>
                <Button variant="outline" onClick={load}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Aggiorna
                </Button>
            </div>

            {loading ? (
                <div className="text-center py-12 text-gray-500">Caricamento...</div>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <KpiCard icon={Users} label="Totale" value={s.total} color="text-blue-500" />
                        <KpiCard icon={TrendingUp} label="Attivi" value={s.active} color="text-indigo-500" />
                        <KpiCard icon={Trophy} label="Vinti" value={s.won} sub={`${s.wonThisMonth} questo mese`} color="text-green-500" />
                        <KpiCard icon={XCircle} label="Persi" value={s.lost} color="text-red-500" />
                        <KpiCard icon={TrendingUp} label="Conversione" value={`${s.conversion}%`} sub="vinti / chiusi" color="text-emerald-500" />
                        <KpiCard icon={Clock} label="Da ricontattare" value={s.recontact} color="text-amber-500" />
                    </div>

                    <Card className="p-4 md:p-5">
                        <h2 className="font-semibold mb-3">Pipeline per stadio</h2>
                        <div className="space-y-2">
                            {LEAD_STAGES.map((st) => (
                                <BarRow
                                    key={st.key}
                                    label={LEAD_STAGE_LABELS[st.key]}
                                    count={s.byStage[st.key] || 0}
                                    total={s.total}
                                    colorClass={st.dotClass}
                                />
                            ))}
                        </div>
                    </Card>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Card className="p-4 md:p-5">
                            <h2 className="font-semibold mb-3">Lead per fonte</h2>
                            {s.bySource.length === 0 ? (
                                <p className="text-sm text-gray-400">Nessun dato.</p>
                            ) : (
                                <div className="space-y-2">
                                    {s.bySource.map((r) => (
                                        <BarRow key={r.key} label={r.label} count={r.count} total={s.total} colorClass="bg-blue-500" />
                                    ))}
                                </div>
                            )}
                        </Card>

                        <Card className="p-4 md:p-5">
                            <h2 className="font-semibold mb-3">Persi per motivo</h2>
                            {s.lostByReason.length === 0 ? (
                                <p className="text-sm text-gray-400">Nessun lead perso con motivo registrato.</p>
                            ) : (
                                <div className="space-y-2">
                                    {s.lostByReason.map((r) => (
                                        <BarRow key={r.key} label={r.label} count={r.count} total={s.lost} colorClass="bg-red-500" />
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <KpiCard icon={Clock} label="Tempo medio chiusura" value={s.avgClose !== null ? `${s.avgClose} gg` : '—'} sub="da creazione a vinto/perso" color="text-purple-500" />
                        <KpiCard icon={Users} label="Nuovi questo mese" value={s.newThisMonth} color="text-blue-500" />
                        <KpiCard icon={Trophy} label="Vinti questo mese" value={s.wonThisMonth} color="text-green-500" />
                    </div>
                </>
            )}
        </div>
    );
}
